import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getApiBaseUrl, readJsonResponse } from '../apiBase';
import {
    AS_REPAIR_STATUS_OPTIONS,
    aggregateAsRepairQtyShare,
    applyAsRepairAutoValuesToForm,
    buildAsRepairListSummary,
    createInitialAsRepairFilters,
    countAsRepairRowsByProgressTab,
    filterAsRepairRows,
    filterAsRepairRowsByShipDateRange,
    filterAsRepairRowsByProgressTab,
    getAsRepairShipDateField,
    asRepairRowToFormValues,
    formatAsRepairFieldDisplayText,
    getAsRepairFilterFieldOptions,
    getAsRepairRowProgressTab,
    getAsRepairRowWorkflowPhase,
    AS_REPAIR_SHIP_COMPLETE_KEY,
    AS_REPAIR_WORKFLOW_PHASE,
    isAsRepairInternalExtraKey,
    isAsRepairRowShipComplete,
    normalizeAsRepairFormConfig,
    withAsRepairShipCompleteFlag,
} from '../utils/asRepairFormConfig';
import { parseAsRepairExcelFile } from '../utils/asRepairExcelImport';
import {
    aggregateAsRepairQtyGroupDetail,
    collectRowIdsForQtyGroup,
} from '../utils/asRepairQtyDetailCategories';
import {
    filterAsRepairRowsByDeviceType,
    getAsRepairDeviceTypeFromRow,
    getAsRepairDeviceTypeLabel,
} from '../utils/asRepairDeviceType';
import AppleSwitchCheckbox from './AppleSwitchCheckbox';
import AsRepairAutoCalculatedField from './AsRepairAutoCalculatedField';
import AsRepairQtyShareChart from './AsRepairQtyShareChart';
import AsRepairQuantityStepper from './AsRepairQuantityStepper';
import SpecDateInput from './SpecDateInput';

const JSON_HEADERS = { 'Content-Type': 'application/json' };
const AS_REPAIR_LIST_PAGE_SIZE = 30;

const STATUS_LABEL_MAP = Object.fromEntries(
    AS_REPAIR_STATUS_OPTIONS.map((o) => [o.value, o.label]),
);

const AsRepairManagePage = ({
    canManageAsRepair = false,
    currentUserName = '',
    asRepairFormConfig,
    showAppToast,
    hideAppToast,
    onNavigateToAdd,
}) => {
    const formConfig = useMemo(
        () => normalizeAsRepairFormConfig(asRepairFormConfig),
        [asRepairFormConfig],
    );
    const listSummary = useMemo(
        () => buildAsRepairListSummary(formConfig),
        [formConfig],
    );
    const labelByKey = useMemo(() => {
        const map = {};
        formConfig.fields.forEach((f) => {
            map[f.key] = f.label;
        });
        return map;
    }, [formConfig]);

    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadError, setLoadError] = useState('');
    const [expandedIds, setExpandedIds] = useState(() => new Set());
    const [specFilters, setSpecFilters] = useState(createInitialAsRepairFilters);
    const [detailDrafts, setDetailDrafts] = useState({});
    const [editingRowIds, setEditingRowIds] = useState(() => new Set());
    const [detailSavingId, setDetailSavingId] = useState(null);
    const [progressTab, setProgressTab] = useState('in_progress');
    const [deletingId, setDeletingId] = useState(null);
    const [excelImportBusy, setExcelImportBusy] = useState(false);
    const [selectedRowIds, setSelectedRowIds] = useState(() => new Set());
    const [bulkDeleting, setBulkDeleting] = useState(false);
    const [shipCompleteBusy, setShipCompleteBusy] = useState(false);
    const [typeFilters, setTypeFilters] = useState({ adjuster: true, controller: true });
    /** 그래프 모델명 클릭 시 목록에만 표시할 행 ID */
    const [chartListFilter, setChartListFilter] = useState(null);
    const [listPage, setListPage] = useState(1);
    const [shipDateFrom, setShipDateFrom] = useState('');
    const [shipDateTo, setShipDateTo] = useState('');
    const excelInputRef = useRef(null);
    const listAnchorRef = useRef(null);

    const filterFieldOptions = useMemo(
        () => getAsRepairFilterFieldOptions(formConfig),
        [formConfig],
    );

    const searchFilteredItems = useMemo(
        () => filterAsRepairRows(items, formConfig, specFilters),
        [items, formConfig, specFilters],
    );

    const shipDateField = useMemo(
        () => getAsRepairShipDateField(formConfig),
        [formConfig],
    );

    const shipDateFilteredItems = useMemo(
        () => filterAsRepairRowsByShipDateRange(searchFilteredItems, formConfig, shipDateFrom, shipDateTo),
        [searchFilteredItems, formConfig, shipDateFrom, shipDateTo],
    );

    const typeFilteredItems = useMemo(
        () => filterAsRepairRowsByDeviceType(shipDateFilteredItems, listSummary, formConfig, typeFilters),
        [shipDateFilteredItems, listSummary, formConfig, typeFilters],
    );

    const chartFilteredBaseItems = useMemo(() => {
        if (!chartListFilter?.rowIds?.length) {
            return typeFilteredItems;
        }
        const idSet = new Set(chartListFilter.rowIds);
        return typeFilteredItems.filter((row) => idSet.has(row.id));
    }, [typeFilteredItems, chartListFilter]);

    const progressCounts = useMemo(
        () => countAsRepairRowsByProgressTab(chartFilteredBaseItems, listSummary, formConfig),
        [chartFilteredBaseItems, listSummary, formConfig],
    );

    const displayedItems = useMemo(
        () => filterAsRepairRowsByProgressTab(chartFilteredBaseItems, listSummary, formConfig, progressTab),
        [chartFilteredBaseItems, listSummary, formConfig, progressTab],
    );

    const listTotalPages = Math.max(1, Math.ceil(displayedItems.length / AS_REPAIR_LIST_PAGE_SIZE));
    const safeListPage = Math.min(listPage, listTotalPages);

    const pagedDisplayedItems = useMemo(() => {
        const start = (safeListPage - 1) * AS_REPAIR_LIST_PAGE_SIZE;
        return displayedItems.slice(start, start + AS_REPAIR_LIST_PAGE_SIZE);
    }, [displayedItems, safeListPage]);

    const qtyShareForSearchResults = useMemo(() => {
        const share = aggregateAsRepairQtyShare(typeFilteredItems, listSummary, formConfig);
        if (!share?.inboundBreakdown?.items) {
            return share;
        }
        return {
            ...share,
            inboundBreakdown: {
                ...share.inboundBreakdown,
                items: share.inboundBreakdown.items.map((item) => {
                    const rowIds = collectRowIdsForQtyGroup(
                        typeFilteredItems,
                        formConfig,
                        listSummary,
                        item.id,
                    );
                    return {
                        ...item,
                        rowIds,
                        rowCount: rowIds.length,
                        subDetail: aggregateAsRepairQtyGroupDetail(
                            typeFilteredItems,
                            formConfig,
                            listSummary,
                            item.id,
                        ),
                    };
                }),
            },
        };
    }, [typeFilteredItems, listSummary, formConfig]);

    const showQtyShareChart = !loading
        && searchFilteredItems.length > 0
        && typeFilteredItems.length > 0;

    const toggleTypeFilter = (key) => {
        setChartListFilter(null);
        setTypeFilters((prev) => {
            const next = { ...prev, [key]: !prev[key] };
            if (!next.adjuster && !next.controller) {
                return prev;
            }
            return next;
        });
    };

    const handleChartListFilter = useCallback((payload) => {
        const selectionKey = payload?.selectionKey;
        if (!selectionKey || !Array.isArray(payload?.rowIds) || payload.rowIds.length === 0) {
            return;
        }
        setChartListFilter((prev) => {
            if (prev?.selectionKey === selectionKey) {
                return null;
            }
            const groupLabel = String(payload.groupLabel || '').trim();
            const categoryTitle = String(payload.categoryTitle || '').trim();
            const modelName = String(payload.modelName || '').trim();
            const rowCount = Number(payload.rowCount) || payload.rowIds.length;
            const labelParts = [groupLabel, categoryTitle, modelName].filter(Boolean);
            return {
                selectionKey,
                rowIds: payload.rowIds,
                label: `${labelParts.join(' · ')} (${rowCount}건)`,
            };
        });
    }, []);

    useEffect(() => {
        setChartListFilter(null);
    }, [specFilters]);

    useEffect(() => {
        if (!chartListFilter || !listAnchorRef.current) {
            return;
        }
        listAnchorRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, [chartListFilter]);

    const displayedRowIds = useMemo(
        () => displayedItems.map((row) => row.id).filter((id) => id != null),
        [displayedItems],
    );

    const isAllDisplayedSelected = displayedRowIds.length > 0
        && displayedRowIds.every((id) => selectedRowIds.has(id));

    const updateSpecFilter = (index, field, value) => {
        setChartListFilter(null);
        setSpecFilters((prev) => prev.map((filter, filterIndex) => (
            filterIndex === index ? { ...filter, [field]: value } : filter
        )));
    };

    const loadItems = useCallback(async () => {
        if (!canManageAsRepair || !currentUserName) {
            setItems([]);
            return;
        }
        setLoading(true);
        setLoadError('');
        try {
            const qs = new URLSearchParams({ requesterName: currentUserName });
            const res = await fetch(`${getApiBaseUrl()}/as-repair-cases?${qs}`, { cache: 'no-store' });
            const data = await readJsonResponse(res);
            setItems(Array.isArray(data) ? data : []);
        } catch (err) {
            setLoadError(err.message || 'A/S 목록을 불러오지 못했습니다.');
            setItems([]);
        } finally {
            setLoading(false);
        }
    }, [canManageAsRepair, currentUserName]);

    useEffect(() => {
        loadItems();
    }, [loadItems]);

    useEffect(() => {
        setSelectedRowIds(new Set());
        setEditingRowIds(new Set());
        setListPage(1);
    }, [progressTab]);

    useEffect(() => {
        setListPage(1);
    }, [chartListFilter, specFilters, typeFilters, shipDateFrom, shipDateTo]);

    useEffect(() => {
        if (listPage > listTotalPages) {
            setListPage(listTotalPages);
        }
    }, [listPage, listTotalPages]);

    const moveListPage = (page) => {
        const next = Math.min(Math.max(1, page), listTotalPages);
        setListPage(next);
        if (listAnchorRef.current) {
            listAnchorRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    };

    const toggleExpanded = (id) => {
        const row = items.find((it) => it.id === id);
        setExpandedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) {
                next.delete(id);
                setEditingRowIds((prevEditing) => {
                    const n = new Set(prevEditing);
                    n.delete(id);
                    return n;
                });
            } else {
                next.add(id);
                if (row) {
                    setDetailDrafts((prevDrafts) => ({
                        ...prevDrafts,
                        [id]: asRepairRowToFormValues(row, formConfig),
                    }));
                }
            }
            return next;
        });
    };

    const startRowEdit = (row, e) => {
        e?.stopPropagation();
        if (!row?.id) {
            return;
        }
        setExpandedIds((prev) => new Set(prev).add(row.id));
        setDetailDrafts((prevDrafts) => ({
            ...prevDrafts,
            [row.id]: asRepairRowToFormValues(row, formConfig),
        }));
        setEditingRowIds((prev) => new Set(prev).add(row.id));
    };

    const handleRowKeyDown = (e, id) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            toggleExpanded(id);
        }
    };

    const patchRowInList = (updatedRow) => {
        setItems((prev) => prev.map((it) => (it.id === updatedRow.id ? updatedRow : it)));
    };

    const reportAsRepairProgress = (label, completed, total) => {
        const safeTotal = Math.max(0, total);
        const pct = safeTotal > 0
            ? Math.min(100, Math.round((completed / safeTotal) * 100))
            : 0;
        showAppToast?.(`${label} ${pct}%`, 'info', { persistent: true });
    };

    const postAsRepairCase = async (values, { excelImport = false } = {}) => {
        const withAuto = applyAsRepairAutoValuesToForm(formConfig.fields, values);
        const res = await fetch(`${getApiBaseUrl()}/as-repair-cases`, {
            method: 'POST',
            headers: JSON_HEADERS,
            body: JSON.stringify({
                requesterName: currentUserName,
                _asRepairPhase: AS_REPAIR_WORKFLOW_PHASE.QTY_CHECK,
                ...(excelImport ? { excelImport: true } : {}),
                ...withAuto,
            }),
        });
        const data = await readJsonResponse(res);
        if (!data || data.id == null) {
            throw new Error('서버 응답이 올바르지 않습니다.');
        }
    };

    const handleExcelFileChange = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (!file || excelImportBusy) {
            return;
        }
        setExcelImportBusy(true);
        reportAsRepairProgress('업로드 중입니다.', 0, 100);
        try {
            reportAsRepairProgress('업로드 중입니다.', 2, 100);
            const { rows, skipped, errors } = await parseAsRepairExcelFile(file, formConfig);
            reportAsRepairProgress('업로드 중입니다.', 5, 100);
            if (errors.length > 0 && rows.length === 0) {
                hideAppToast?.();
                showAppToast?.(errors[0], 'error');
                return;
            }
            if (rows.length === 0) {
                hideAppToast?.();
                showAppToast?.('등록할 데이터가 없습니다.', 'error');
                return;
            }
            let successCount = 0;
            let failCount = 0;
            const totalRows = rows.length;
            const uploadSpan = 95;
            for (let index = 0; index < rows.length; index += 1) {
                try {
                    await postAsRepairCase(rows[index], { excelImport: true });
                    successCount += 1;
                } catch {
                    failCount += 1;
                }
                const completedUnits = 5 + Math.round(((index + 1) / totalRows) * uploadSpan);
                reportAsRepairProgress('업로드 중입니다.', completedUnits, 100);
            }
            reportAsRepairProgress('업로드 중입니다.', 100, 100);
            await loadItems();
            hideAppToast?.();
            if (successCount > 0) {
                const skipMsg = skipped > 0 ? ` (빈 행 ${skipped}건 제외)` : '';
                const failMsg = failCount > 0 ? `, 실패 ${failCount}건` : '';
                showAppToast?.(
                    `엑셀에서 A/S 목록 ${successCount}건을 등록했습니다.${skipMsg}${failMsg}`,
                    failCount > 0 ? 'error' : 'success',
                );
            } else {
                showAppToast?.('엑셀 데이터 등록에 실패했습니다.', 'error');
            }
        } catch (err) {
            hideAppToast?.();
            showAppToast?.(err.message || '엑셀 파일을 읽지 못했습니다.', 'error');
        } finally {
            setExcelImportBusy(false);
        }
    };

    const toggleRowSelected = (rowId, checked) => {
        setSelectedRowIds((prev) => {
            const next = new Set(prev);
            if (checked) {
                next.add(rowId);
            } else {
                next.delete(rowId);
            }
            return next;
        });
    };

    const handleToggleSelectAllDisplayed = () => {
        const allSelected = displayedRowIds.length > 0
            && displayedRowIds.every((id) => selectedRowIds.has(id));
        setSelectedRowIds((prev) => {
            const next = new Set(prev);
            if (allSelected) {
                displayedRowIds.forEach((id) => next.delete(id));
            } else {
                displayedRowIds.forEach((id) => next.add(id));
            }
            return next;
        });
    };

    const handleBulkDeleteSelected = async () => {
        if (selectedRowIds.size === 0) {
            showAppToast?.('삭제할 목록을 선택해주세요.', 'error');
            return;
        }
        const ok = window.confirm(`선택한 A/S 목록 ${selectedRowIds.size}건을 삭제하시겠습니까?`);
        if (!ok) {
            return;
        }
        setBulkDeleting(true);
        const ids = [...selectedRowIds];
        const total = ids.length;
        let successCount = 0;
        let failCount = 0;
        reportAsRepairProgress('삭제 중입니다.', 0, total);
        try {
            for (let index = 0; index < ids.length; index += 1) {
                const id = ids[index];
                try {
                    const res = await fetch(`${getApiBaseUrl()}/as-repair-cases/${id}`, {
                        method: 'DELETE',
                        headers: JSON_HEADERS,
                        body: JSON.stringify({ requesterName: currentUserName }),
                    });
                    await readJsonResponse(res);
                    successCount += 1;
                } catch {
                    failCount += 1;
                }
                reportAsRepairProgress('삭제 중입니다.', index + 1, total);
            }
            setSelectedRowIds(new Set());
            setExpandedIds(new Set());
            await loadItems();
            hideAppToast?.();
            if (successCount > 0) {
                const failMsg = failCount > 0 ? `, 실패 ${failCount}건` : '';
                showAppToast?.(`선택한 A/S 목록 ${successCount}건을 삭제했습니다.${failMsg}`, failCount > 0 ? 'error' : 'success');
            } else {
                showAppToast?.('선택한 목록 삭제에 실패했습니다.', 'error');
            }
        } catch {
            hideAppToast?.();
            showAppToast?.('선택한 목록 삭제에 실패했습니다.', 'error');
        } finally {
            setBulkDeleting(false);
        }
    };

    const handleDeleteRow = async (row) => {
        const label = listSummary.columns
            .map((col) => formatAsRepairFieldDisplayText(col.field, row, formConfig))
            .filter((t) => t && t !== '-')
            .slice(0, 2)
            .join(' / ');
        const ok = window.confirm(
            label
                ? `「${label}」 A/S 목록을 삭제하시겠습니까?`
                : '이 A/S 목록을 삭제하시겠습니까?',
        );
        if (!ok) {
            return;
        }
        setDeletingId(row.id);
        try {
            const res = await fetch(`${getApiBaseUrl()}/as-repair-cases/${row.id}`, {
                method: 'DELETE',
                headers: JSON_HEADERS,
                body: JSON.stringify({ requesterName: currentUserName }),
            });
            await readJsonResponse(res);
            setItems((prev) => prev.filter((it) => it.id !== row.id));
            setExpandedIds((prev) => {
                const next = new Set(prev);
                next.delete(row.id);
                return next;
            });
            setEditingRowIds((prev) => {
                const next = new Set(prev);
                next.delete(row.id);
                return next;
            });
            showAppToast?.('A/S 목록이 삭제되었습니다.', 'success');
        } catch (err) {
            showAppToast?.(err.message || '삭제에 실패했습니다.', 'error');
        } finally {
            setDeletingId(null);
        }
    };

    const updateDetailDraftField = (rowId, key, value) => {
        setDetailDrafts((prev) => {
            const base = prev[rowId]
                || asRepairRowToFormValues(items.find((it) => it.id === rowId) || {}, formConfig);
            const nextValues = applyAsRepairAutoValuesToForm(formConfig.fields, {
                ...base,
                [key]: value,
            });
            return {
                ...prev,
                [rowId]: nextValues,
            };
        });
    };

    const buildRowSavePayload = (row) => {
        const draft = detailDrafts[row.id] || asRepairRowToFormValues(row, formConfig);
        const withAuto = applyAsRepairAutoValuesToForm(formConfig.fields, draft);
        const payload = {
            requesterName: currentUserName,
            ...withAuto,
        };
        formConfig.fields.forEach((field) => {
            if (field.inputType === 'quantity') {
                const v = payload[field.key];
                if (v === 0 || v === '0') {
                    payload[field.key] = '';
                }
            }
        });
        if (getAsRepairRowWorkflowPhase(row) === AS_REPAIR_WORKFLOW_PHASE.QTY_CHECK) {
            payload._asRepairPhase = AS_REPAIR_WORKFLOW_PHASE.IN_PROGRESS;
        }
        if (isAsRepairRowShipComplete(row)) {
            payload[AS_REPAIR_SHIP_COMPLETE_KEY] = true;
        }
        return payload;
    };

    const handleBulkShipComplete = async () => {
        if (progressTab !== 'done') {
            showAppToast?.('완료 탭에서 선택한 목록만 출고 완료 처리할 수 있습니다.', 'error');
            return;
        }
        if (selectedRowIds.size === 0) {
            showAppToast?.('출고 완료할 목록을 선택해주세요.', 'error');
            return;
        }
        const targetRows = displayedItems.filter((row) => selectedRowIds.has(row.id));
        const invalid = targetRows.filter((row) => (
            getAsRepairRowProgressTab(row, listSummary, formConfig) !== 'done'
        ));
        if (invalid.length > 0) {
            showAppToast?.('완료 탭에 있는 목록만 출고 완료 처리할 수 있습니다.', 'error');
            return;
        }
        const ok = window.confirm(`선택한 A/S 목록 ${targetRows.length}건을 출고 완료 처리하시겠습니까?`);
        if (!ok) {
            return;
        }
        setShipCompleteBusy(true);
        const ids = targetRows.map((row) => row.id);
        try {
            const res = await fetch(`${getApiBaseUrl()}/as-repair-cases/mark-ship-complete`, {
                method: 'POST',
                headers: JSON_HEADERS,
                body: JSON.stringify({
                    requesterName: currentUserName,
                    ids,
                }),
            });
            const data = await readJsonResponse(res);
            const updatedRows = Array.isArray(data?.updated) ? data.updated : [];
            const updatedById = new Map(updatedRows.map((row) => [Number(row.id), row]));
            setItems((prev) => prev.map((row) => {
                const fromServer = updatedById.get(Number(row.id));
                if (fromServer) {
                    return fromServer;
                }
                if (ids.includes(Number(row.id))) {
                    return withAsRepairShipCompleteFlag(row);
                }
                return row;
            }));
            setSelectedRowIds(new Set());
            const count = Number(data?.count) || updatedRows.length || ids.length;
            showAppToast?.(`선택한 A/S 목록 ${count}건을 출고 완료로 이동했습니다.`, 'success');
            setProgressTab('ship_complete');
        } catch (err) {
            showAppToast?.(err.message || '출고 완료 처리에 실패했습니다.', 'error');
        } finally {
            setShipCompleteBusy(false);
        }
    };

    const handleRowSave = async (row, e) => {
        e?.stopPropagation();
        if (!row?.id || detailSavingId != null) {
            return;
        }
        setDetailSavingId(row.id);
        try {
            const res = await fetch(`${getApiBaseUrl()}/as-repair-cases/${row.id}`, {
                method: 'PUT',
                headers: JSON_HEADERS,
                body: JSON.stringify(buildRowSavePayload(row)),
            });
            const updated = await readJsonResponse(res);
            patchRowInList(updated);
            setDetailDrafts((prev) => ({
                ...prev,
                [row.id]: asRepairRowToFormValues(updated, formConfig),
            }));
            setEditingRowIds((prev) => {
                const next = new Set(prev);
                next.delete(row.id);
                return next;
            });
            const wasQtyCheck = getAsRepairRowWorkflowPhase(row) === AS_REPAIR_WORKFLOW_PHASE.QTY_CHECK;
            const nowDone = filterAsRepairRowsByProgressTab(
                [updated],
                listSummary,
                formConfig,
                'done',
            ).length > 0;
            if (wasQtyCheck) {
                showAppToast?.('저장되어 진행중 목록으로 이동합니다.', 'success');
            } else if (nowDone && progressTab !== 'done') {
                showAppToast?.('전체 수량과 수리완료 수량이 일치하여 완료 목록으로 이동합니다.', 'success');
            } else {
                showAppToast?.('저장했습니다.', 'success');
            }
        } catch (err) {
            showAppToast?.(err.message || '저장에 실패했습니다.', 'error');
        } finally {
            setDetailSavingId(null);
        }
    };

    const renderReadOnlyFieldValue = (field, row) => {
        const text = formatAsRepairFieldDisplayText(field, row, formConfig);
        return text === '' ? '-' : text;
    };

    const renderDetailFields = (row, isEditing) => {
        const formValues = asRepairRowToFormValues(row, formConfig);
        const draftValues = detailDrafts[row.id] || formValues;
        const shouldAlwaysShowDetailField = (field) => {
            const label = String(field?.label || '').replace(/\s+/g, '');
            return label.includes('실입고수량') || label.includes('진행중수량');
        };
        const orderedFields = formConfig.fields.filter((f) => (
            !listSummary.summaryKeys.has(f.key)
            || f.inputType === 'textarea'
            || f.inputType === 'text'
            || f.inputType === 'quantity'
            || shouldAlwaysShowDetailField(f)
        ));

        const nodes = orderedFields
            .map((f) => {
                const isWide = f.inputType === 'textarea';
                if (!isEditing) {
                    if (f.inputType === 'auto') {
                        return (
                            <AsRepairAutoCalculatedField
                                key={f.key}
                                id={`as-repair-detail-auto-${row.id}-${f.key}`}
                                label={f.label}
                                field={f}
                                formConfig={formConfig}
                                values={formValues}
                            />
                        );
                    }
                    return (
                        <div
                            key={f.key}
                            className={`spec-view-item${isWide ? ' full-width' : ''}`}
                        >
                            <label>{f.label}</label>
                            <div className="spec-view-value">{renderReadOnlyFieldValue(f, row)}</div>
                        </div>
                    );
                }
                if (f.inputType === 'quantity') {
                    const val = draftValues[f.key] ?? '';
                    return (
                        <AsRepairQuantityStepper
                            key={f.key}
                            id={`as-repair-detail-qty-${row.id}-${f.key}`}
                            label={f.label}
                            value={val == null || val === '' ? '' : val}
                            onChange={(next) => updateDetailDraftField(row.id, f.key, next)}
                            disabled={detailSavingId === row.id}
                            showControls
                        />
                    );
                }
                if (f.inputType === 'auto') {
                    return (
                        <AsRepairAutoCalculatedField
                            key={f.key}
                            id={`as-repair-detail-auto-${row.id}-${f.key}`}
                            label={f.label}
                            field={f}
                            formConfig={formConfig}
                            values={draftValues}
                        />
                    );
                }
                const val = draftValues[f.key] ?? '';
                if (f.inputType === 'status') {
                    return (
                        <div key={f.key} className={`spec-view-item${isWide ? ' full-width' : ''}`}>
                            <label htmlFor={`as-repair-detail-${row.id}-${f.key}`}>{f.label}</label>
                            <select
                                id={`as-repair-detail-${row.id}-${f.key}`}
                                className="field-input"
                                value={val || 'received'}
                                onChange={(e) => updateDetailDraftField(row.id, f.key, e.target.value)}
                                disabled={detailSavingId === row.id}
                            >
                                {AS_REPAIR_STATUS_OPTIONS.map((opt) => (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                            </select>
                        </div>
                    );
                }
                if (f.inputType === 'date') {
                    return (
                        <div key={f.key} className={`spec-view-item${isWide ? ' full-width' : ''}`}>
                            <label htmlFor={`as-repair-detail-${row.id}-${f.key}`}>{f.label}</label>
                            <SpecDateInput
                                value={val}
                                onChange={(next) => updateDetailDraftField(row.id, f.key, next)}
                                disabled={detailSavingId === row.id}
                                className="field-input spec-date-input"
                            />
                        </div>
                    );
                }
                return (
                    <div
                        key={f.key}
                        className={`spec-view-item${isWide ? ' full-width' : ''}`}
                    >
                        <label htmlFor={`as-repair-detail-${row.id}-${f.key}`}>{f.label}</label>
                        {isWide ? (
                            <textarea
                                id={`as-repair-detail-${row.id}-${f.key}`}
                                className="field-input as-repair-add-textarea"
                                value={val}
                                onChange={(e) => updateDetailDraftField(row.id, f.key, e.target.value)}
                                disabled={detailSavingId === row.id}
                                rows={3}
                            />
                        ) : (
                            <input
                                id={`as-repair-detail-${row.id}-${f.key}`}
                                type="text"
                                className="field-input"
                                value={val}
                                onChange={(e) => updateDetailDraftField(row.id, f.key, e.target.value)}
                                disabled={detailSavingId === row.id}
                            />
                        )}
                    </div>
                );
            })
            .filter(Boolean);

        const extra = row.extra && typeof row.extra === 'object' ? row.extra : {};
        const configuredKeys = new Set(formConfig.fields.map((f) => f.key));
        const extraNodes = Object.keys(extra)
            .filter((k) => {
                if (isAsRepairInternalExtraKey(k)) {
                    return false;
                }
                if (configuredKeys.has(k)) {
                    return false;
                }
                const v = extra[k];
                if (v === 0) {
                    return true;
                }
                return v != null && String(v).trim() !== '';
            })
            .map((key) => (
                <div key={key} className="spec-view-item full-width">
                    <label>{labelByKey[key] || key}</label>
                    <div className="spec-view-value">{String(extra[key])}</div>
                </div>
            ));

        if (nodes.length === 0 && extraNodes.length === 0) {
            return <p className="empty-list-message">추가 세부 항목이 없습니다.</p>;
        }

        return (
            <>
                {nodes.length > 0 ? (
                    <div
                        className="as-repair-detail-fields-grid"
                        onClick={(e) => e.stopPropagation()}
                        onKeyDown={(e) => e.stopPropagation()}
                        role="presentation"
                    >
                        {nodes}
                    </div>
                ) : null}
                {extraNodes.length > 0 ? (
                    <div className="spec-view-grid">
                        {extraNodes}
                    </div>
                ) : null}
            </>
        );
    };

    if (!canManageAsRepair) {
        return (
            <div className="as-repair-manage-page">
                <section className="model-list-section model-list-section--asRepair">
                    <div className="model-list-head">
                        <h3>A/S 수리 관리</h3>
                    </div>
                    <p className="as-repair-manage-help">A/S 수리 관리 권한이 없습니다. 권한 관리에서 권한을 부여받아야 이용할 수 있습니다.</p>
                </section>
            </div>
        );
    }

    return (
        <div className="as-repair-manage-page">
            <section className="model-list-section model-list-section--asRepair">
                <div className="model-list-head as-repair-model-list-head">
                    <div className="as-repair-model-list-head-top">
                        <h3>A/S 수리 관리</h3>
                        <div className="model-list-head-right as-repair-head-actions">
                            {shipDateField ? (
                                <div className="as-repair-ship-date-range" onClick={(e) => e.stopPropagation()}>
                                    <span className="as-repair-ship-date-range-label">출고일자</span>
                                    <SpecDateInput
                                        value={shipDateFrom}
                                        onChange={setShipDateFrom}
                                        disabled={excelImportBusy}
                                        className="field-input spec-date-input as-repair-ship-date-input"
                                        title={`${shipDateField.label} 시작일`}
                                    />
                                    <span className="as-repair-ship-date-sep">~</span>
                                    <SpecDateInput
                                        value={shipDateTo}
                                        onChange={setShipDateTo}
                                        disabled={excelImportBusy}
                                        className="field-input spec-date-input as-repair-ship-date-input"
                                        title={`${shipDateField.label} 종료일`}
                                    />
                                </div>
                            ) : null}
                            <input
                                ref={excelInputRef}
                                type="file"
                                accept=".xlsx,.xlsb,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel.sheet.binary.macroEnabled.12"
                                className="as-repair-excel-input"
                                onChange={handleExcelFileChange}
                                disabled={excelImportBusy}
                                aria-hidden
                                tabIndex={-1}
                            />
                            <button
                                type="button"
                                className="button button-cancel"
                                onClick={() => excelInputRef.current?.click()}
                                disabled={excelImportBusy}
                            >
                                {excelImportBusy ? '업로드 중…' : '엑셀 파일 업로드'}
                            </button>
                            <button
                                type="button"
                                className="button"
                                onClick={() => onNavigateToAdd?.()}
                                disabled={excelImportBusy}
                            >
                                A/S 목록 추가
                            </button>
                        </div>
                    </div>
                    <div className="as-repair-progress-tabs" role="tablist" aria-label="A/S 진행 상태">
                        <button
                            type="button"
                            role="tab"
                            aria-selected={progressTab === 'qty_check'}
                            className={`as-repair-progress-tab${progressTab === 'qty_check' ? ' is-active' : ''}`}
                            onClick={() => setProgressTab('qty_check')}
                        >
                            수량 확인중
                            <span className="as-repair-progress-tab-count">{progressCounts.qtyCheck}</span>
                        </button>
                        <button
                            type="button"
                            role="tab"
                            aria-selected={progressTab === 'in_progress'}
                            className={`as-repair-progress-tab${progressTab === 'in_progress' ? ' is-active' : ''}`}
                            onClick={() => setProgressTab('in_progress')}
                        >
                            진행중
                            <span className="as-repair-progress-tab-count">{progressCounts.inProgress}</span>
                        </button>
                        <button
                            type="button"
                            role="tab"
                            aria-selected={progressTab === 'done'}
                            className={`as-repair-progress-tab${progressTab === 'done' ? ' is-active' : ''}`}
                            onClick={() => setProgressTab('done')}
                        >
                            완료
                            <span className="as-repair-progress-tab-count">{progressCounts.done}</span>
                        </button>
                        <button
                            type="button"
                            role="tab"
                            aria-selected={progressTab === 'ship_complete'}
                            className={`as-repair-progress-tab${progressTab === 'ship_complete' ? ' is-active' : ''}`}
                            onClick={() => setProgressTab('ship_complete')}
                        >
                            출고 완료
                            <span className="as-repair-progress-tab-count">{progressCounts.shipComplete}</span>
                        </button>
                    </div>
                </div>
                <p className="as-repair-manage-help">
                    새 목록은 「수량 확인중」에 생성됩니다. 목록에서 「수정」 후 「저장」하면 「진행중」으로 이동합니다.
                    전체 수량과 수리완료 수량이 같으면 「완료」에 표시됩니다. 「완료」 탭에서 선택 후 「출고 완료」를 누르면 「출고 완료」 탭으로 이동합니다.
                </p>

                {loadError && <div className="error-box">{loadError}</div>}

                <div className="search-bar-wrap as-repair-search-bar">
                    <div className="search-filters-left as-repair-search-filters">
                        {specFilters.map((filter, index) => (
                            <div key={index} className="spec-filter-group">
                                <select
                                    className="spec-filter-select"
                                    value={filter.fieldKey}
                                    onChange={(e) => updateSpecFilter(index, 'fieldKey', e.target.value)}
                                >
                                    <option value="">항목 선택</option>
                                    {filterFieldOptions.map((opt) => (
                                        <option key={opt.key} value={opt.key}>{opt.label}</option>
                                    ))}
                                </select>
                                <input
                                    className="spec-filter-input"
                                    type="text"
                                    value={filter.query}
                                    onChange={(e) => updateSpecFilter(index, 'query', e.target.value)}
                                    placeholder="값 검색"
                                />
                            </div>
                        ))}
                    </div>
                </div>

                {!loading && searchFilteredItems.length > 0 ? (
                    <div className="as-repair-list-selection-bar">
                        <div className="as-repair-list-selection-left">
                            <div className="type-filter-buttons as-repair-type-filter-buttons">
                                <button
                                    type="button"
                                    className={`button type-filter-button adjuster${typeFilters.adjuster ? '' : ' off'}`}
                                    onClick={() => toggleTypeFilter('adjuster')}
                                    disabled={bulkDeleting || excelImportBusy}
                                >
                                    조절기
                                </button>
                                <button
                                    type="button"
                                    className={`button type-filter-button controller${typeFilters.controller ? '' : ' off'}`}
                                    onClick={() => toggleTypeFilter('controller')}
                                    disabled={bulkDeleting || excelImportBusy}
                                >
                                    제어기
                                </button>
                            </div>
                        </div>
                        <div className="as-repair-list-selection-right">
                            {progressTab === 'done' ? (
                                <button
                                    type="button"
                                    className="button"
                                    onClick={handleBulkShipComplete}
                                    disabled={bulkDeleting || excelImportBusy || shipCompleteBusy || selectedRowIds.size === 0}
                                >
                                    {shipCompleteBusy
                                        ? '처리중…'
                                        : `출고 완료${selectedRowIds.size > 0 ? ` (${selectedRowIds.size})` : ''}`}
                                </button>
                            ) : null}
                            <button
                                type="button"
                                className="mailbox-select-all-text"
                                onClick={handleToggleSelectAllDisplayed}
                                disabled={bulkDeleting || excelImportBusy || shipCompleteBusy}
                            >
                                {isAllDisplayedSelected ? '전체 해제' : '전체 선택'}
                            </button>
                            <button
                                type="button"
                                className="button button-cancel"
                                onClick={handleBulkDeleteSelected}
                                disabled={bulkDeleting || excelImportBusy || shipCompleteBusy || selectedRowIds.size === 0}
                            >
                                {bulkDeleting ? '삭제중…' : `선택 삭제${selectedRowIds.size > 0 ? ` (${selectedRowIds.size})` : ''}`}
                            </button>
                        </div>
                    </div>
                ) : null}

                {showQtyShareChart ? (
                    <AsRepairQtyShareChart
                        share={qtyShareForSearchResults}
                        rowCount={typeFilteredItems.length}
                        selectedSelectionKey={chartListFilter?.selectionKey ?? null}
                        onListFilter={handleChartListFilter}
                    />
                ) : null}

                {chartListFilter ? (
                    <div className="as-repair-chart-list-filter-bar">
                        <span className="as-repair-chart-list-filter-label">
                            목록 필터: <strong>{chartListFilter.label}</strong>
                        </span>
                        <button
                            type="button"
                            className="button button-cancel as-repair-chart-list-filter-clear"
                            onClick={() => setChartListFilter(null)}
                        >
                            필터 해제
                        </button>
                    </div>
                ) : null}

                <div ref={listAnchorRef} className="as-repair-list-anchor" aria-hidden />

                {loading ? (
                    <p className="empty-list-message">목록을 불러오는 중…</p>
                ) : items.length === 0 ? (
                    <p className="empty-list-message">등록된 A/S 목록이 없습니다. 「A/S 목록 추가」로 등록해주세요.</p>
                ) : searchFilteredItems.length === 0 ? (
                    <p className="empty-list-message">검색 조건에 맞는 A/S 목록이 없습니다.</p>
                ) : typeFilteredItems.length === 0 ? (
                    <p className="empty-list-message">선택한 조절기·제어기 필터에 맞는 A/S 목록이 없습니다.</p>
                ) : chartListFilter && chartFilteredBaseItems.length === 0 ? (
                    <p className="empty-list-message">그래프에서 선택한 항목·모델에 해당하는 A/S 목록이 없습니다.</p>
                ) : displayedItems.length === 0 ? (
                    <p className="empty-list-message">
                        {chartListFilter
                            ? '선택한 항목·모델에 해당하는 목록이 현재 탭에 없습니다. 다른 탭을 확인하거나 필터를 해제해주세요.'
                            : progressTab === 'ship_complete'
                                ? '출고 완료된 A/S 목록이 없습니다. 완료 탭에서 목록을 선택한 뒤 「출고 완료」를 누르면 이곳에 표시됩니다.'
                                : progressTab === 'done'
                                    ? '완료된 A/S 목록이 없습니다. 전체 수량과 수리완료 수량이 같아지면 이곳에 표시됩니다.'
                                    : progressTab === 'in_progress'
                                    ? '진행중인 A/S 목록이 없습니다. 수량 확인중에서 「수정」 후 「저장」하면 이곳에 표시됩니다.'
                                    : '수량 확인중인 A/S 목록이 없습니다.'}
                    </p>
                ) : (
                    <>
                    {pagedDisplayedItems.map((row) => {
                        const expanded = expandedIds.has(row.id);
                        const deviceType = getAsRepairDeviceTypeFromRow(row, listSummary, formConfig);
                        const deviceLabel = getAsRepairDeviceTypeLabel(deviceType);
                        const isEditing = editingRowIds.has(row.id);
                        return (
                            <div key={row.id} id={`as-repair-row-${row.id}`}>
                                <div
                                    className={`model-name model-name-general as-repair-list-row as-repair-list-row--selectable model-name-with-selector${expanded ? ' as-repair-list-row--open' : ''}`}
                                    onClick={() => toggleExpanded(row.id)}
                                    onKeyDown={(e) => handleRowKeyDown(e, row.id)}
                                    role="button"
                                    tabIndex={0}
                                    aria-expanded={expanded}
                                    aria-controls={`as-repair-detail-${row.id}`}
                                >
                                    <span className="model-row-selector-wrap">
                                        <AppleSwitchCheckbox
                                            checked={selectedRowIds.has(row.id)}
                                            disabled={bulkDeleting || deletingId === row.id || excelImportBusy || shipCompleteBusy}
                                            onClick={(e) => e.stopPropagation()}
                                            onChange={(e) => toggleRowSelected(row.id, e.target.checked)}
                                            aria-label="A/S 목록 선택"
                                        />
                                    </span>
                                    {deviceLabel ? (
                                        <span
                                            className={`as-repair-device-type-badge as-repair-device-type-badge--${deviceType}`}
                                            title={deviceLabel}
                                        >
                                            {deviceLabel}
                                        </span>
                                    ) : null}
                                    <div className="as-repair-list-main-cols">
                                        {listSummary.columns.map((col) => {
                                            const text = formatAsRepairFieldDisplayText(col.field, row, formConfig);
                                            return (
                                                <div
                                                    key={col.id}
                                                    className="as-repair-summary-item"
                                                    title={`${col.displayLabel}: ${text}`}
                                                >
                                                    <span className="as-repair-summary-label">{col.displayLabel}:</span>
                                                    <span className="as-repair-summary-value">{text}</span>
                                                </div>
                                            );
                                        })}
                                    </div>
                                    <div
                                        className="as-repair-row-actions"
                                        onClick={(e) => e.stopPropagation()}
                                        onKeyDown={(e) => e.stopPropagation()}
                                        role="presentation"
                                    >
                                        {isEditing ? (
                                            <>
                                                <button
                                                    type="button"
                                                    className="button"
                                                    disabled={
                                                        detailSavingId === row.id
                                                        || deletingId === row.id
                                                        || bulkDeleting
                                                        || excelImportBusy
                                                        || shipCompleteBusy
                                                    }
                                                    onClick={(e) => handleRowSave(row, e)}
                                                >
                                                    {detailSavingId === row.id ? '저장중…' : '저장'}
                                                </button>
                                                <button
                                                    type="button"
                                                    className="button button-cancel as-repair-row-delete-btn"
                                                    disabled={
                                                        deletingId === row.id
                                                        || detailSavingId === row.id
                                                        || bulkDeleting
                                                        || excelImportBusy
                                                        || shipCompleteBusy
                                                    }
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleDeleteRow(row);
                                                    }}
                                                >
                                                    {deletingId === row.id ? '삭제중…' : '삭제'}
                                                </button>
                                            </>
                                        ) : (
                                            <button
                                                type="button"
                                                className="button"
                                                disabled={
                                                    deletingId === row.id
                                                    || bulkDeleting
                                                    || excelImportBusy
                                                    || shipCompleteBusy
                                                }
                                                onClick={(e) => startRowEdit(row, e)}
                                            >
                                                수정
                                            </button>
                                        )}
                                    </div>
                                </div>
                                {expanded ? (
                                    <div
                                        id={`as-repair-detail-${row.id}`}
                                        className="as-repair-expanded-detail"
                                        onClick={(e) => e.stopPropagation()}
                                        onKeyDown={(e) => e.stopPropagation()}
                                        role="presentation"
                                    >
                                        {renderDetailFields(row, isEditing)}
                                    </div>
                                ) : null}
                            </div>
                        );
                    })}
                    {listTotalPages > 1 ? (
                        <div className="pagination-wrap as-repair-list-pagination">
                            <button
                                type="button"
                                className="pagination-button"
                                onClick={() => moveListPage(safeListPage - 1)}
                                disabled={safeListPage === 1}
                            >
                                이전
                            </button>
                            {Array.from({ length: listTotalPages }, (_, index) => index + 1).map((page) => (
                                <button
                                    key={page}
                                    type="button"
                                    className={`pagination-button${page === safeListPage ? ' active' : ''}`}
                                    onClick={() => moveListPage(page)}
                                >
                                    {page}
                                </button>
                            ))}
                            <button
                                type="button"
                                className="pagination-button"
                                onClick={() => moveListPage(safeListPage + 1)}
                                disabled={safeListPage === listTotalPages}
                            >
                                다음
                            </button>
                        </div>
                    ) : null}
                    </>
                )}
            </section>
        </div>
    );
};

export default AsRepairManagePage;
